
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useApiClient } from "@/lib/api";
import { ArrowLeft, Play, Pause, Mic, MicOff, Volume2, VolumeX, MessageSquare, Loader2 } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

interface Question {
  id: string;
  question: string;
  audio_url?: string;
  is_asked: boolean;
}

interface Job {
  id: string;
  title: string;
  company: string;
  description: string;
}

const Practice = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { api } = useApiClient();

  const [job, setJob] = useState<Job | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isGeneratingQuestions, setIsGeneratingQuestions] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [textResponse, setTextResponse] = useState("");
  const [responseMethod, setResponseMethod] = useState<"text" | "voice">("text");

  useEffect(() => {
    const fetchJobAndQuestions = async () => {
      try {
        // In a real app:
        // const jobData = await api.getJobById(jobId);
        // setJob(jobData);

        // const questionsData = await api.getQuestionsByJobId(jobId);
        // if (questionsData && questionsData.length > 0) {
        //   setQuestions(questionsData);
        // }

        // Mock data
        setTimeout(() => {
          setJob({
            id: "1",
            title: "Frontend Developer",
            company: "Tech Innovations Inc.",
            description: "We're looking for a skilled Frontend Developer...",
          });

          setQuestions([
            {
              id: "q1",
              question: "Can you describe a challenging project you've worked on and how you overcame technical obstacles?",
              audio_url: "",
              is_asked: false,
            },
            {
              id: "q2",
              question: "How do you stay updated with the latest frontend technologies and best practices?",
              audio_url: "",
              is_asked: false,
            },
            {
              id: "q3",
              question: "Explain your experience with responsive design and how you approach making websites work across different devices.",
              audio_url: "",
              is_asked: false,
            },
          ]);
        }, 1000);
      } catch (error) {
        console.error("Error fetching job and questions:", error);
        toast.error("Error loading interview data");
      }
    };

    if (jobId) {
      fetchJobAndQuestions();
    }
  }, [jobId]);

  const generateQuestions = async () => {
    setIsGeneratingQuestions(true);
    try {
      // In a real app:
      // const newQuestions = await api.generateQuestions({
      //   job_id: jobId,
      //   count: 5,
      // });
      // setQuestions(newQuestions);

      // Mock generation - simulating API delay
      toast.info("Generating interview questions...");
      setTimeout(() => {
        setQuestions([
          {
            id: "q1",
            question: "Can you describe a challenging project you've worked on and how you overcame technical obstacles?",
            audio_url: "",
            is_asked: false,
          },
          {
            id: "q2",
            question: "How do you stay updated with the latest frontend technologies and best practices?",
            audio_url: "",
            is_asked: false,
          },
          {
            id: "q3",
            question: "Explain your experience with responsive design and how you approach making websites work across different devices.",
            audio_url: "",
            is_asked: false,
          },
          {
            id: "q4",
            question: "Describe how you would optimize a React application for performance.",
            audio_url: "",
            is_asked: false,
          },
          {
            id: "q5",
            question: "How do you handle cross-browser compatibility issues in your projects?",
            audio_url: "",
            is_asked: false,
          },
        ]);
        setIsGeneratingQuestions(false);
        toast.success("Interview questions generated successfully!");
      }, 3000);
    } catch (error) {
      console.error("Error generating questions:", error);
      toast.error("Failed to generate interview questions");
      setIsGeneratingQuestions(false);
    }
  };

  const playQuestion = () => {
    if (!questions[currentQuestionIndex]) return;

    // Mark the question as asked
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === currentQuestionIndex ? { ...q, is_asked: true } : q
      )
    );

    // Simulate text-to-speech
    setIsPlaying(true);
    toast.info("Playing question audio...");

    // In a real app, would play the audio file
    setTimeout(() => {
      setIsPlaying(false);
      toast.success("Question played");
    }, 3000);
  };

  const toggleRecording = () => {
    if (isRecording) {
      // Stop recording
      setIsRecording(false);
      toast.success("Recording saved");
    } else {
      // Start recording
      setIsRecording(true);
      toast.info("Recording your response...");
    }
  };

  const handleNextQuestion = () => {
    setTextResponse("");
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      toast.success("Interview completed!");
    }
  };

  const handlePreviousQuestion = () => {
    setTextResponse("");
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const getCurrentQuestion = () => {
    return questions[currentQuestionIndex] || null;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/jobs')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Jobs
          </Button>
          <h1 className="text-3xl font-bold">Interview Practice</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {job && (
              <Card>
                <CardHeader>
                  <CardTitle>{job.title}</CardTitle>
                  <CardDescription>{job.company}</CardDescription>
                </CardHeader>
              </Card>
            )}

            {!questions.length ? (
              <Card className="min-h-[400px] flex flex-col justify-center items-center py-8">
                <CardContent className="text-center space-y-6">
                  <h2 className="text-xl font-semibold">No Interview Questions Yet</h2>
                  <p className="text-gray-500 max-w-md mx-auto">
                    Generate interview questions based on this job description and your resume to start practicing
                  </p>
                  <Button
                    onClick={generateQuestions}
                    disabled={isGeneratingQuestions}
                    className="min-w-[200px]"
                  >
                    {isGeneratingQuestions ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Generating Questions...
                      </>
                    ) : (
                      <>
                        Generate Questions
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader className="border-b">
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle>Question {currentQuestionIndex + 1}/{questions.length}</CardTitle>
                      <CardDescription>
                        Answer as you would in a real interview
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handlePreviousQuestion}
                        disabled={currentQuestionIndex === 0}
                      >
                        Previous
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleNextQuestion}
                        disabled={currentQuestionIndex === questions.length - 1}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="bg-gray-50 p-6 rounded-lg mb-6 min-h-[120px] flex items-center">
                    <div className="flex-1">
                      {getCurrentQuestion()?.is_asked ? (
                        <p className="text-lg">{getCurrentQuestion()?.question}</p>
                      ) : (
                        <div className="flex flex-col items-center justify-center text-center space-y-4">
                          <p className="text-gray-500">Click play to hear the interview question</p>
                          <Button
                            onClick={playQuestion}
                            disabled={isPlaying}
                            className="btn-primary"
                          >
                            {isPlaying ? (
                              <>
                                <Pause className="mr-2 h-4 w-4" />
                                Playing...
                              </>
                            ) : (
                              <>
                                <Play className="mr-2 h-4 w-4" />
                                Play Question
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </div>

                    <div className="ml-4">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          toast.info(isPlaying ? "Audio paused" : "Replaying question");
                          setIsPlaying(!isPlaying);
                        }}
                        disabled={!getCurrentQuestion()?.is_asked}
                      >
                        {isPlaying ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-medium mb-3">Response Method</h3>
                      <RadioGroup
                        defaultValue="text"
                        className="flex gap-4"
                        value={responseMethod}
                        onValueChange={(value) => setResponseMethod(value as "text" | "voice")}
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="text" id="text" />
                          <Label htmlFor="text" className="flex items-center">
                            <MessageSquare className="h-4 w-4 mr-2" />
                            Text
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="voice" id="voice" />
                          <Label htmlFor="voice" className="flex items-center">
                            <Mic className="h-4 w-4 mr-2" />
                            Voice
                          </Label>
                        </div>
                      </RadioGroup>
                    </div>

                    {responseMethod === "text" ? (
                      <div className="space-y-4">
                        <Textarea
                          placeholder="Type your response here..."
                          className="min-h-[150px] interview-input"
                          value={textResponse}
                          onChange={(e) => setTextResponse(e.target.value)}
                        />
                        <Button
                          className="w-full"
                          disabled={textResponse.length < 10}
                        >
                          Save Response
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="border border-gray-200 rounded-lg p-8 flex flex-col items-center justify-center min-h-[150px]">
                          {isRecording ? (
                            <div className="flex flex-col items-center space-y-4">
                              <div className="relative">
                                <div className="absolute -inset-1 bg-red-500 rounded-full opacity-75 animate-pulse"></div>
                                <Mic className="h-12 w-12 text-red-500 relative" />
                              </div>
                              <p>Recording... (click to stop)</p>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center space-y-4">
                              <MicOff className="h-12 w-12 text-gray-400" />
                              <p className="text-gray-500">Click to start recording</p>
                            </div>
                          )}
                        </div>
                        <Button
                          onClick={toggleRecording}
                          variant={isRecording ? "destructive" : "default"}
                          className="w-full"
                        >
                          {isRecording ? (
                            <>
                              <Pause className="mr-2 h-4 w-4" />
                              Stop Recording
                            </>
                          ) : (
                            <>
                              <Mic className="mr-2 h-4 w-4" />
                              Start Recording
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <div>
            <Card>
              <CardHeader>
                <CardTitle>Interview Progress</CardTitle>
                <CardDescription>
                  Track your progress through the interview questions
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {questions.length > 0 ? (
                    <div>
                      <div className="flex justify-between mb-2">
                        <span>Questions Completed:</span>
                        <span className="font-bold">
                          {questions.filter(q => q.is_asked).length} / {questions.length}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div
                          className="bg-interview-blue h-2.5 rounded-full"
                          style={{ width: `${(questions.filter(q => q.is_asked).length / questions.length) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-center text-gray-500 py-2">
                      No questions generated yet
                    </p>
                  )}

                  <div className="border-t pt-4 mt-4">
                    <h3 className="font-medium mb-3">Question List</h3>
                    <ul className="space-y-2">
                      {questions.map((q, index) => (
                        <li
                          key={q.id}
                          className={`p-2 rounded text-sm cursor-pointer ${index === currentQuestionIndex
                              ? 'bg-interview-blue text-white'
                              : q.is_asked
                                ? 'bg-gray-100'
                                : ''
                            }`}
                          onClick={() => setCurrentQuestionIndex(index)}
                        >
                          <div className="flex items-center">
                            <span className="mr-2">{index + 1}.</span>
                            <span className="truncate">{q.question.length > 40 ? `${q.question.substring(0, 40)}...` : q.question}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={generateQuestions}
                  disabled={isGeneratingQuestions}
                >
                  {isGeneratingQuestions ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      Regenerate Questions
                    </>
                  )}
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Practice;
